import { isTrackingOptedOut } from '~/utils/tracking-consent';

// Meta (Facebook) pixel bootstrap. Client-only (`.client.ts`). Mirrors the
// `matomo.client.ts` shape: read runtime config, early-return when unconfigured,
// inject an async script tag, and fire page views off the `page:finish` hook.
//
// The pixel is disabled entirely (no script injected, `window.fbq` left
// undefined) when the pixel id is blank OR the visitor has opted out. We never
// use `fbq('consent','revoke')` — that still loads the script; opting out must
// mean nothing is fetched at all.

interface FbqStub {
  (...args: unknown[]): void;
  callMethod?: (...args: unknown[]) => void;
  push: unknown;
  loaded: boolean;
  version: string;
  queue: unknown[];
}

export default defineNuxtPlugin((nuxtApp) => {
  const config = useRuntimeConfig();
  const pixelId = config.public.metaPixelId as string;

  // Graceful disable when unconfigured (matches Matomo's early-return).
  if (!pixelId) return;

  // Never-inject gate: if the visitor has opted out, load nothing at all.
  if (isTrackingOptedOut()) return;

  // Standard Meta pixel bootstrap stub — queues calls until fbevents.js loads.
  if (!window.fbq) {
    const fbq = function (...args: unknown[]): void {
      if (fbq.callMethod) {
        fbq.callMethod(...args);
      } else {
        fbq.queue.push(args);
      }
    } as FbqStub;

    window.fbq = fbq;
    if (!window._fbq) window._fbq = fbq;
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = '2.0';
    fbq.queue = [];
  }

  window.fbq!('init', pixelId);

  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  document.head.appendChild(script);

  // Fire PageView only on route completion — not in the init snippet — so the
  // first load doesn't emit a duplicate PageView.
  nuxtApp.hook('page:finish', () => {
    window.fbq?.('track', 'PageView');
  });
});
